import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PRINT_AGENT_CONFIG } from '../constants/print-agent.config';
import { ShowMessageService } from './message';
import {
  AgentJobStatus,
  IAgentPrinter,
  isJobFinished,
  isPrinterBlocked,
  isPrinterBusy,
  PrintAgentRequestError,
  PrintAgentService,
  PrintAgentUnavailableError,
} from './print-agent.service';

export interface IPrintRequest {
  blob: Blob;
  fileName: string;
  /** Bir hujjatni takroriy yuborishni aniqlash uchun barqaror kalit (fileId, ZIP ichidagi yo'l). */
  docKey: string;
  printer: IAgentPrinter;
  /** Band printerda bo'sh muqobilni taklif qilish uchun. */
  printers: IAgentPrinter[];
}

export interface ITrackedJob {
  localId: number;
  jobId: string;
  docKey: string;
  fileName: string;
  printer: string;
  status: AgentJobStatus;
  position: number | null;
  pagesPrinted: number | null;
  totalPages: number | null;
  error: string | null;
  sentAt: number;
  /** Qayta urinish uchun; ish ro'yxatdan olib tashlanganda yo'qoladi. */
  blob: Blob;
}

export interface IPrintPromptButton {
  label: string;
  value: string;
  severity?: 'primary' | 'secondary' | 'success' | 'warn' | 'danger';
  outlined?: boolean;
}

export interface IPrintPrompt {
  title: string;
  message: string;
  hint?: string;
  buttons: IPrintPromptButton[];
  resolve: (value: string | null) => void;
}

@Injectable({ providedIn: 'root' })
export class PrintFlowService {
  private readonly agent = inject(PrintAgentService);
  private readonly message = inject(ShowMessageService);
  private readonly transloco = inject(TranslocoService);

  readonly guideVisible = signal(false);
  readonly prompt = signal<IPrintPrompt | null>(null);
  readonly jobs = signal<ITrackedJob[]>([]);
  readonly widgetVisible = signal(false);
  readonly activeCount = computed(
    () => this.jobs().filter((j) => !isJobFinished(j.status)).length,
  );

  private localIdSeq = 0;
  /** Takroriy yuborishni aniqlash uchun; vidjet tozalansa ham saqlanadi. */
  private recentSends: {
    localId: number;
    docKey: string;
    printer: string;
    sentAt: number;
  }[] = [];
  /** Agentga ketma-ket ulanib bo'lmagan so'rovlar soni (ish bo'yicha). */
  private pollFailures = new Map<number, number>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  t(key: string, fallback: string, params?: Record<string, unknown>): string {
    const value = this.transloco.translate(key, params);
    if (value !== key) {
      return value;
    }
    return fallback.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) =>
      params && params[name] !== undefined ? String(params[name]) : '',
    );
  }

  openGuide(): void {
    this.guideVisible.set(true);
  }

  /** true — agent ishni qabul qildi. */
  async print(request: IPrintRequest): Promise<boolean> {
    let printer = request.printer;

    if (isPrinterBlocked(printer)) {
      this.message.warning(
        this.t(
          'print_printer_unavailable',
          "Bu printerga hozir yuborib bo'lmaydi",
        ),
      );
      return false;
    }

    const duplicate = this.findRecent(request.docKey);
    if (duplicate) {
      const minutes = Math.max(
        1,
        Math.round((Date.now() - duplicate.sentAt) / 60000),
      );
      const answer = await this.ask({
        title: this.t('print_duplicate_title', 'Hujjat yaqinda yuborilgan'),
        message: this.t(
          'print_duplicate_message',
          'Bu hujjat {{minutes}} daqiqa oldin {{printer}} printeriga yuborilgan. Yana chop etasizmi?',
          { minutes, printer: duplicate.printer },
        ),
        buttons: [
          {
            label: this.t('no', "Yo'q"),
            value: 'no',
            severity: 'secondary',
            outlined: true,
          },
          { label: this.t('print_again', 'Ha, yana chop etish'), value: 'yes' },
        ],
      });
      if (answer !== 'yes') {
        return false;
      }
    }

    if (isPrinterBusy(printer)) {
      const free = request.printers.find(
        (p) =>
          p.name !== printer.name && !isPrinterBlocked(p) && !isPrinterBusy(p),
      );
      const buttons: IPrintPromptButton[] = [
        {
          label: this.t('Cancel', 'Bekor qilish'),
          value: 'cancel',
          severity: 'secondary',
          outlined: true,
        },
      ];
      if (free) {
        buttons.push({
          label: this.t('print_send_to', '{{printer}} ga yuborish', {
            printer: free.name,
          }),
          value: 'free',
          severity: 'success',
        });
      }
      buttons.push({
        label: this.t('print_add_to_queue', "Navbatga qo'shish"),
        value: 'queue',
      });

      const answer = await this.ask({
        title: this.t('print_busy_title', 'Printer band'),
        message:
          printer.jobCount > 0
            ? this.t(
                'print_busy_message',
                "{{printer}} printerida hozir {{count}} ta ish navbatda. Hujjatingiz navbatga qo'yiladi va ulardan keyin chop etiladi.",
                { printer: printer.name, count: printer.jobCount },
              )
            : this.t(
                'print_busy_message_no_count',
                "{{printer}} printeri hozir boshqa hujjatni chop etyapti. Hujjatingiz navbatga qo'yiladi.",
                { printer: printer.name },
              ),
        hint: free
          ? this.t('print_free_hint', "{{printer}} hozir bo'sh.", {
              printer: free.name,
            })
          : undefined,
        buttons,
      });

      if (answer === 'free' && free) {
        printer = free;
        this.agent.savePrinter(free.name);
      } else if (answer !== 'queue') {
        return false;
      }
    }

    return this.send(
      request.blob,
      request.fileName,
      request.docKey,
      printer.name,
    );
  }

  async cancel(job: ITrackedJob): Promise<void> {
    const answer = await this.ask({
      title: this.t('print_cancel_title', 'Ishni bekor qilish'),
      message: this.t(
        'print_cancel_message',
        '"{{file}}" ni chop etish bekor qilinsinmi?',
        { file: job.fileName },
      ),
      buttons: [
        {
          label: this.t('no', "Yo'q"),
          value: 'no',
          severity: 'secondary',
          outlined: true,
        },
        {
          label: this.t('print_cancel_confirm', 'Ha, bekor qilish'),
          value: 'yes',
          severity: 'danger',
        },
      ],
    });
    if (answer !== 'yes') {
      return;
    }

    try {
      await this.agent.cancelJob(job.jobId);
      this.recentSends = this.recentSends.filter(
        (r) => r.localId !== job.localId,
      );
      this.patchJob(job.localId, { status: 'cancelled' });
    } catch (e) {
      this.handleError(e);
    }
  }

  async retry(job: ITrackedJob): Promise<void> {
    let printers: IAgentPrinter[];
    try {
      printers = (await this.agent.getPrinters()).printers.filter(
        (p) => !isPrinterBlocked(p),
      );
    } catch (e) {
      this.handleError(e);
      return;
    }

    if (!printers.length) {
      this.message.warning(
        this.t('printer_none_available', "Ishlaydigan printer yo'q"),
      );
      return;
    }

    let target = printers[0].name;
    if (printers.length > 1) {
      const answer = await this.ask({
        title: this.t('print_retry_title', 'Qayta chop etish'),
        message: this.t(
          'print_retry_message',
          '"{{file}}" qaysi printerga yuborilsin?',
          { file: job.fileName },
        ),
        buttons: [
          {
            label: this.t('Cancel', 'Bekor qilish'),
            value: '',
            severity: 'secondary',
            outlined: true,
          },
          ...printers.slice(0, 4).map((p) => ({
            label: p.name,
            value: p.name,
            severity: (p.name === job.printer
              ? 'secondary'
              : 'primary') as IPrintPromptButton['severity'],
          })),
        ],
      });
      if (!answer) {
        return;
      }
      target = answer;
    }

    this.removeJob(job.localId);
    await this.send(job.blob, job.fileName, job.docKey, target);
  }

  removeJob(localId: number): void {
    this.jobs.update((list) => list.filter((j) => j.localId !== localId));
    if (!this.jobs().length) {
      this.widgetVisible.set(false);
    }
  }

  closeWidget(): void {
    this.widgetVisible.set(false);
    this.jobs.update((list) => list.filter((j) => !isJobFinished(j.status)));
  }

  answerPrompt(value: string | null): void {
    const current = this.prompt();
    this.prompt.set(null);
    current?.resolve(value);
  }

  private async send(
    blob: Blob,
    fileName: string,
    docKey: string,
    printer: string,
  ): Promise<boolean> {
    try {
      const job = await this.agent.print(blob, printer);
      const localId = ++this.localIdSeq;
      this.recentSends.push({ localId, docKey, printer, sentAt: Date.now() });
      this.jobs.update((list) => [
        {
          localId,
          jobId: job.jobId,
          docKey,
          fileName,
          printer: job.printer ?? printer,
          status: job.status === 'unknown' ? 'queued' : job.status,
          position: job.position,
          pagesPrinted: job.pagesPrinted,
          totalPages: job.totalPages,
          error: job.error,
          sentAt: Date.now(),
          blob,
        },
        ...list,
      ]);
      this.widgetVisible.set(true);
      this.message.successMsg(
        this.t('print_sent_to', '{{printer}} printeriga yuborildi', {
          printer,
        }),
      );
      this.ensurePolling();
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  }

  private handleError(e: unknown): void {
    if (e instanceof PrintAgentUnavailableError) {
      this.openGuide();
      return;
    }
    if (e instanceof PrintAgentRequestError && e.message) {
      this.message.errorMsg(e.message);
      return;
    }
    this.message.errorMsg(
      this.t(
        'print_failed',
        "Chop etib bo'lmadi. Print Agent ishlayotganini va printer ulanganini tekshiring",
      ),
    );
  }

  private findRecent(docKey: string) {
    const since = Date.now() - PRINT_AGENT_CONFIG.duplicateWindowMs;
    this.recentSends = this.recentSends.filter((r) => r.sentAt >= since);
    return this.recentSends.find((r) => r.docKey === docKey);
  }

  private ask(prompt: Omit<IPrintPrompt, 'resolve'>): Promise<string | null> {
    this.prompt()?.resolve(null);
    return new Promise((resolve) => this.prompt.set({ ...prompt, resolve }));
  }

  private patchJob(localId: number, patch: Partial<ITrackedJob>): void {
    this.jobs.update((list) =>
      list.map((j) => (j.localId === localId ? { ...j, ...patch } : j)),
    );
    this.scheduleAutoHide();
  }

  private ensurePolling(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.pollTimer) {
      return;
    }
    this.pollTimer = setInterval(
      () => void this.poll(),
      PRINT_AGENT_CONFIG.jobsPollMs,
    );
  }

  private async poll(): Promise<void> {
    const active = this.jobs().filter(
      (j) => !isJobFinished(j.status) && j.jobId,
    );
    if (!active.length) {
      this.stopPolling();
      return;
    }

    await Promise.all(
      active.map(async (job) => {
        try {
          const fresh = await this.agent.getJob(job.jobId);
          this.pollFailures.delete(job.localId);
          this.patchJob(job.localId, {
            status: fresh.status,
            position: fresh.position,
            pagesPrinted: fresh.pagesPrinted,
            totalPages: fresh.totalPages,
            error: fresh.error,
          });
        } catch (e) {
          // Agent qayta ishga tushgan bo'lsa ish unutiladi — holatini endi bilib bo'lmaydi.
          if (e instanceof PrintAgentRequestError && e.status === 404) {
            this.patchJob(job.localId, { status: 'unknown' });
          } else if (e instanceof PrintAgentUnavailableError) {
            // Agent yopilgan: ~30 soniya kutib, keyin holatni noma'lum deb belgilaymiz.
            const failures = (this.pollFailures.get(job.localId) ?? 0) + 1;
            this.pollFailures.set(job.localId, failures);
            if (failures >= 15) {
              this.pollFailures.delete(job.localId);
              this.patchJob(job.localId, { status: 'unknown' });
            }
          }
        }
      }),
    );
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.scheduleAutoHide();
  }

  private scheduleAutoHide(): void {
    if (this.activeCount() > 0 || this.hideTimer || !this.widgetVisible()) {
      return;
    }
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.activeCount() === 0) {
        this.closeWidget();
      }
    }, PRINT_AGENT_CONFIG.widgetAutoHideMs);
  }
}
